import { useRef, useEffect } from 'react'
import { Factory, Users, Globe, Target, CheckCircle2, Play, Building, Award, TrendingUp, Shield, ChevronRight } from 'lucide-react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import CertificationImage from '../static/认证资质.jpg'

const stats = [
  { icon: Factory, value: '15+', label: '年行业经验' },
  { icon: Users, value: '100000+', label: '服务客户' },
  { icon: Globe, value: '30+', label: '覆盖省份' },
  { icon: Target, value: '99%', label: '客户满意度' },
]

const coreValues = [
  { 
    icon: Building, 
    title: '企业使命', 
    description: '致力于提供高效、节能、环保的炉业解决方案，为客户创造价值，为社会贡献力量。'
  },
  { 
    icon: TrendingUp, 
    title: '发展愿景', 
    description: '成为行业领先的清洁能源解决方案提供商，打造国际知名品牌。'
  },
  { 
    icon: Award, 
    title: '核心价值观', 
    description: '科技创新、质量为本、客户至上、诚信经营、和谐发展。'
  },
  { 
    icon: Shield, 
    title: '质量承诺', 
    description: '严格执行国际质量管理体系，确保每一件产品都符合高品质标准。'
  },
]

const companyHistory = [
  { year: '2016', event: '山东云净环保科技有限公司成立，初始注册资本1000万元' },
  { year: '2020', event: '注册资本增加至5500万元' },
  { year: '2023', event: '被认定为高新技术企业、山东省创新型中小企业' },
  { year: '2023', event: '成立吉林分公司，"云净炉业"商标注册成功' },
  { year: '2024', event: '成立天津分公司，注册资本调整至2000万元' },
  { year: '2024', event: '完成"云净炉业"商标注册' },
  { year: '2025', event: '获得"科技型中小企业"资质，成立庆阳分公司' },
  { year: '2025', event: '提交"火烈鸟"、"火灵珠"、"天天顺"、"福小暖"等商标注册申请' },
]

const promotionalVideos = [
  { id: 'water-heater-showcase', name: '水暖炉展示', href: '#' },
  { id: 'air-heater-showcase', name: '风暖炉展示', href: '#' },
  { id: 'burner-showcase', name: '燃烧机展示', href: '#' },
  { id: 'wood-fireplace-showcase', name: '燃木壁炉展示', href: '#' },
]

export default function AboutPage() {
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect()
        }
      },
      { threshold: 0.2 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-700 to-primary-900 text-white py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">关于云净炉业</h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              专注清洁能源炉具研发与制造，为您提供高效、环保的加热解决方案
            </p>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-sm font-semibold mb-4">
                公司简介
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900 mb-6">
                专注炉业，匠心品质
              </h2>
              <p className="text-secondary-600 mb-6 leading-relaxed">
                云净炉业有限公司成立于2009年，总部位于中国制造业重镇。公司专注于清洁能源利用技术的研发与应用，
                产品涵盖风暖炉、水暖炉、燃烧机、燃木壁炉等多个系列，广泛应用于工业加热、农业烘干、商业供暖等领域。
              </p>
              <p className="text-secondary-600 mb-8 leading-relaxed">
                我们始终坚持"科技创新、质量为本、客户至上"的经营理念，不断引进国内外先进技术，
                建立了完善的质量管理体系和售后服务网络，为客户提供从产品选型、方案设计到安装调试、运维培训的一站式服务。
              </p>
              <a href="/company-detail" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors">
                了解更多
                <ChevronRight className="w-5 h-5" />
              </a>
            </div>
            
            {/* Right: Image */}
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={CertificationImage}
                  alt="认证资质"
                  className="w-full h-full object-contain"
                />
              </div>
              {/* Floating stats */}
              <div className="absolute -bottom-8 -left-8 bg-white rounded-xl p-5 shadow-xl border border-secondary-100">
                <div className="text-3xl font-bold text-primary-600">15+</div>
                <div className="text-sm text-secondary-500">年行业经验</div>
              </div>
              <div className="absolute -top-8 -right-8 bg-white rounded-xl p-5 shadow-xl border border-secondary-100">
                <div className="text-3xl font-bold text-primary-600">100000+</div>
                <div className="text-sm text-secondary-500">服务客户</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-secondary-100 text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 mb-4">
                  <stat.icon className="w-6 h-6 text-primary-600" />
                </div>
                <div className="text-3xl font-bold text-secondary-900 mb-2">{stat.value}</div>
                <div className="text-sm text-secondary-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-sm font-semibold mb-4">
              企业理念
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900 mb-4">
              我们的核心价值观
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              引领行业发展，创造美好生活
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreValues.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-secondary-100"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-6">
                  <value.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-4">{value.title}</h3>
                <p className="text-secondary-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company History */}
      <section className="py-20 lg:py-28 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-sm font-semibold mb-4">
              发展历程
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900 mb-4">
              公司发展历程
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              从创立到成为行业领先，我们的成长足迹
            </p>
          </div>

          <div className="relative">
            {/* Timeline */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-primary-200" />
            
            <div className="space-y-12">
              {companyHistory.map((item, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-12 text-right' : 'pl-12'}`}>
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-secondary-100">
                      <div className="text-lg font-bold text-primary-600 mb-2">{item.year}</div>
                      <div className="text-secondary-700">{item.event}</div>
                    </div>
                  </div>
                  <div className="z-10 flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div className="w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Promotional Videos Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-sm font-semibold mb-4">
              品牌展示
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900 mb-4">
              云净宣传片
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              了解我们的产品优势和企业实力
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {promotionalVideos.map((video) => (
              <div key={video.id} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-secondary-100">
                <div className="aspect-video bg-gray-200 relative flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-primary-600/90 flex items-center justify-center cursor-pointer hover:bg-primary-700 transition-colors">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                </div>
                <div className="p-4">
                  <h4 className="font-semibold text-secondary-900 mb-2">{video.name}</h4>
                  <a href={video.href} className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 transition-colors text-sm font-medium">
                    观看视频
                    <Play className="w-3 h-3" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  )
}